import React, { useState, useEffect } from 'react';
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride';

const TOUR_KEY = 'balancescript_tour_completed';

const steps: Step[] = [
  {
    target: '.account-switcher',
    content: 'This is your account switcher. You can have multiple accounts and switch between them here.',
    disableBeacon: true,
    placement: 'bottom',
  },
  {
    target: '.register-table thead',
    content: 'Your transactions appear here in date order. Each row shows the payee, amount, and a running balance.',
    placement: 'bottom',
  },
  {
    target: '.input-row',
    content: 'Add new transactions at the bottom. Select an action type, enter the payee and amount, then click the green button.',
    placement: 'top',
  },
  {
    target: '.col-clr',
    content: 'Check the box when a transaction clears your bank. Cleared rows turn green so you can track reconciliation.',
    placement: 'left',
  },
  {
    target: 'button[title="Help"]',
    content: 'Need help? Click here anytime to open the user guide with detailed instructions and screenshots.',
    placement: 'bottom',
  },
  {
    target: '.fui-Avatar',
    content: 'Click your profile to access account sharing, data import/export, user management, and sign out.',
    placement: 'bottom',
  },
];

interface GuidedTourProps {
  ready: boolean;
}

export const GuidedTour: React.FC<GuidedTourProps> = ({ ready }) => {
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (ready) {
      const completed = localStorage.getItem(TOUR_KEY);
      if (!completed) {
        // Small delay to let the UI render before starting tour
        const timer = setTimeout(() => setRun(true), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [ready]);

  const handleCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem(TOUR_KEY, 'true');
    }
  };

  if (!run) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      callback={handleCallback}
      locale={{
        back: 'Back',
        close: 'Got it',
        last: 'Done',
        next: 'Next',
        skip: 'Skip tour',
      }}
      styles={{
        options: {
          primaryColor: '#2c5282',
          zIndex: 3000,
          arrowColor: '#fff',
          backgroundColor: '#fff',
          textColor: '#1a1a2e',
        },
        tooltip: {
          borderRadius: 12,
          padding: '20px 24px',
          fontSize: 14,
        },
        tooltipContent: {
          padding: '8px 0',
          lineHeight: 1.6,
        },
        buttonNext: {
          borderRadius: 8,
          padding: '8px 20px',
          fontSize: 14,
          fontWeight: 600,
        },
        buttonBack: {
          color: '#6b7280',
          fontSize: 14,
        },
        buttonSkip: {
          color: '#9ca3af',
          fontSize: 13,
        },
        spotlight: {
          borderRadius: 8,
        },
      }}
    />
  );
};
